import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { dragHook, useForceSimulation, useZoom } from './forceUtils';

/**
 * Renders a simple node-link diagram.
 *
 * Expects: {
 *   nodes: [],
 *   links: [],
 * }
 *
 * @param data
 * @param dimensions
 * @returns {*}
 * @constructor
 */
export const InnovationGraph = ({ data, dimensions }) => {
  const { nodes, links } = data;
  const svgRef = useRef(null);
  const nodeGroupRef = useRef(null);
  const linkGroupRef = useRef(null);
  const containerRef = useRef(null);

  const [forces, setForces] = useState([]);

  const colorScale = d3.scaleSequential(d3.interpolateRainbow);

  const centroidScale = useMemo(() => {
    const extent = d3.extent(nodes, node => node.count);
    return d3.scaleLinear().domain(extent).range([10, 10]);
  }, [nodes]);

  const { simulation, tick } = useForceSimulation({ forces, data: nodes, dimensions });
  useZoom({ svgRef, containerRef });

  useEffect(() => {
    const forces = [
      { name: 'center', force: d3.forceCenter(dimensions.width / 2, dimensions.height / 2) },
      { name: 'charge', force: d3.forceManyBody().strength(-200) },
      { name: 'x', force: d3.forceX(dimensions.width / 2).strength(.5) },
      { name: 'y', force: d3.forceY(dimensions.height / 2).strength(1) },
      { name: 'links', force: d3.forceLink(links).id(d => d.id).strength(.5).distance(17) },
    ];

    setForces(forces);
  }, [links, dimensions, centroidScale]);

  useEffect(() => {
    // render proposal nodes
    const proposals = d3.select(nodeGroupRef.current)
      .selectAll('.proposal')
      .data(nodes.filter(d => d.type === 'proposal'), d => d.label)
      .join(enter => {
          const group = enter.append('g')
            .classed('proposal', true)
            .on('mouseover', function(d) {
              d3.select(this)
                .select('circle')
                .raise()
                .transition()
                .duration(250)
                .attr('fill', 'white')
                .attr('stroke', 'white');

              d3.select(this)
                .select('.nodeLabel')
                .transition().duration(250)
                .style('opacity', 1.0);

              d3.select(nodeGroupRef.current)
                .selectAll('.category')
                .filter(c => c.id === d.parentId)
                .select('.shape')
                .transition()
                .duration(250)
                .attr('fill', 'white')
                .attr('stroke', 'white');

            })
            .on('mouseout', function(d) {
              d3.select(this)
                .select('circle')
                .transition()
                .duration(250)
                .attr('fill', 'black')
                .attr('stroke', 'black');

              d3.select(this).select('.nodeLabel')
                .transition().duration(250)
                .style('opacity', 0);

              d3.select(nodeGroupRef.current)
                .selectAll('.category')
                .filter(c => c.id === d.parentId)
                .select('.shape')
                .transition()
                .duration(250)
                .attr('fill', d => colorScale(d.colorIndex / d.maxParents))
                .attr('stroke', d => colorScale(d.colorIndex / d.maxParents));
            });

          group.append('circle')
            .attr('r', 3)
            .attr('fill', 'black')
            .attr('strokeWidth', 0.5)
            .attr('stroke', 'black');

          // create the labels, hidden by default
          const textGroup = group.append('g')
            .classed('nodeLabel', true)
            .attr('transform', `translate(0 -20)`)
            .style('opacity', 0)
            .style('pointer-events', 'none');

          // fake label so the following path can get a real size
          textGroup.append('text')
            .text(d => d.label)
            .attr('font-size', 12)
            .attr('text-anchor', 'middle')
            .attr('stroke', 'none')
            .attr('fill', 'none');

          // label background
          textGroup.append('path')
            .attr('d', function(d) {
              const { width, height } = d3.select(this.parentNode).select('text').node().getBBox();
              const upperLeft = [-width / 2 - 4, -height / 2 - 5];
              return `M ${upperLeft[0]},${upperLeft[1]} 
          l ${width + 8},0 
          l 0,${height + 8} 
          l ${-width / 2},0 
          l -4,4 
          l -4,-4 
          l ${-width / 2},0`;
            });

          // label itself
          textGroup.append('text')
            .text(d => d.label)
            .attr('z-index', 1)
            .attr('font-size', 12)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('stroke', 'none')
            .attr('fill', 'white');

        },
        update => update.attr('transform', d => `translate(${d.x} ${d.y})`),
        exit => exit.remove());

    // set up dragging
    proposals.call(dragHook(simulation));

    // render category nodes
    const categories = d3.select(nodeGroupRef.current)
      .selectAll('.category')
      .data(nodes.filter(d => d.type === 'category'), d => d.label)
      .join(enter => {
          const group = enter.append('g')
            .classed('category', true)
            .on('mouseover', function(d) {
              d3.select(this).select('.shape')
                .transition()
                .duration(250)
                .attr('fill', 'white')
                .attr('stroke', 'white');

              d3.select(this).select('.label')
                .transition()
                .duration(250)
                .attr('font-size', 16)
                .attr('font-weight', 'bolder');

              d3.select(nodeGroupRef.current)
                .selectAll('.proposal')
                .filter(p => p.parentId === d.id)
                .selectAll("circle")
                .transition()
                .duration(250)
                .attr('fill', 'white')
                .attr('stroke', 'white');
            })
            .on('mouseout', function(d) {
              d3.select(this).select('.shape')
                .transition()
                .duration(250)
                .attr('fill', d => colorScale(d.colorIndex / d.maxParents))
                .attr('stroke', d => colorScale(d.colorIndex / d.maxParents));

              d3.select(nodeGroupRef.current)
                .selectAll('.proposal')
                .filter(p => p.parentId === d.id)
                .selectAll("circle")
                .transition()
                .duration(250)
                .attr('fill', 'black')
                .attr('stroke', 'black');

              d3.select(this).select('.label')
                .transition()
                .duration(500)
                .attr('font-size', 12)
                .attr('font-weight', 'normal')
                .attr('fill', 'white');
            });

          group.append('circle')
            .classed('shape', true)
            .attr('r', d => centroidScale(d.count))
            .attr('fill', d => colorScale(d.colorIndex / d.maxParents))
            .attr('strokeWidth', 0.5)
            .attr('stroke', d => colorScale(d.colorIndex / d.maxParents));

          group.append('text')
            .classed('label', true)
            .text(d => d.label)
            .attr('y', d => centroidScale(d.count) + 5)
            .attr('fill', 'white')
            .attr('font-size', '12')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'hanging');
        },
        update => update.attr('transform', d => `translate(${d.x} ${d.y})`),
        exit => exit.remove());

    // set up dragging
    categories.call(dragHook(simulation));

  }, [nodeGroupRef, linkGroupRef, data, tick, simulation, nodes, colorScale, centroidScale]);

  return (
    <svg width={dimensions.width} height={dimensions.height} ref={svgRef} style={{
      background: 'linear-gradient(98deg, rgba(6,35,46,1) 0%, rgba(36,89,109,1) 50%, rgba(6,35,46,1) 100%)',
    }}>
      <g ref={containerRef}>
        <g ref={nodeGroupRef}/>
        <g ref={linkGroupRef}/>
      </g>
    </svg>
  );
};
